/**
 * What happens when the ~5 MB localStorage box is full.
 *
 * Before #695 the answer was a `window.confirm('Storage is full. Clear all app
 * data to continue?')` that deleted `persist:root` — the pantry and the
 * unsynced ratings outbox, the two things with no other copy. The "cleanup"
 * it tried first freed nothing (it only removed `debug_`/`temp_` keys nothing
 * ever writes), so the dialog was the only branch that ever ran.
 *
 * This is a ladder instead. Each failed write goes one rung further down, and
 * every rung costs the cook only state that exists somewhere else:
 *
 *   1. drop the legacy translation blob (3 MB, mirrored in Firestore, #695)
 *   2. shed the "mirrors" — slices re-seeded from the cloud on the next boot
 *   3. shed the "lists" — the same, but the cook notices them missing
 *   4. report, keep going in memory, never offer to delete anything
 *
 * A rung cannot retry the write that failed: those bytes are the serialized
 * OLD state. Shedding marks slices as evicted from disk and flushes, which
 * re-enters this engine — synchronously — with the trimmed blob. The ladder
 * watches that nested write directly rather than trusting `persistor.flush()`'s
 * promise, which redux-persist resolves even when the write failed.
 *
 * The ladder itself runs from a microtask, never on the failing write's call
 * stack. An explicit `persistor.flush()` (a rating tap, onboarding, the
 * page-hide flush) runs storage from inside redux-persist's FLUSH reducer,
 * where dispatching the shed throws "Reducers may not dispatch actions" —
 * exactly the writes the ladder exists to save. Deferring costs one microtask
 * and makes the engine re-entrant, so writes are serialised: one ladder at a
 * time, later writes for the same key coalesced behind it (redux-persist's
 * writes are whole-state snapshots, so only the latest matters).
 *
 * Kept free of redux: the store registers a handler (`registerLowStorageHandler`)
 * the same way the ratings outbox registers its persist flush, because the
 * storage engine is created while the store is still being built.
 */

/** The subset of `window.localStorage` the ladder needs — injectable for tests. */
export interface SyncStorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Tiers, cheapest first. Which slices each one names lives with the reducers
 * in `src/redux/storageShedding.ts`; the engine only knows the order.
 */
export type ShedTier = 'mirrors' | 'lists';
export const SHED_TIERS: readonly ShedTier[] = ['mirrors', 'lists'];

export type BudgetRung = 'legacy-blob' | ShedTier | 'exhausted';

/** Raw keys that are safe to delete outright: mirrored in Firestore, never read since #695. */
export const LEGACY_LOCAL_STORAGE_KEYS = ['recipe_translations_v2', 'recipe_translations'] as const;

/** How many keys a report names, largest first. Enough to see the tenant, not the whole box. */
const REPORTED_KEYS = 8;

/**
 * A key name in a report is the tenant, not the key. Firebase names its auth
 * entry `firebase:authUser:<web API key>:[DEFAULT]`; the first two segments say
 * everything the report needs and nothing analytics should carry.
 */
export const reportableKeyName = (key: string): string => key.split(':').slice(0, 2).join(':');

export interface QuotaExceededReport {
  key: string;
  rung: BudgetRung;
  /** Whether this rung got the state onto disk. */
  recovered: boolean;
  writeBytes: number;
  totalBytes: number;
  largestKeys: Array<[string, number]>;
  error: string;
}

export interface DeviceStorageBudgetDeps {
  /**
   * Evict one tier from disk and rewrite, synchronously — `persistor.flush()`
   * is. A rewrite that arrives later is invisible to the ladder, which will
   * have moved on to the next rung by then. Called from a microtask, so it
   * may dispatch.
   */
  shed: (tier: ShedTier) => void;
  report: (report: QuotaExceededReport) => void;
}

/** Every spelling of "the box is full" across browsers. */
export const isQuotaExceeded = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  const code = (error as { code?: unknown }).code;
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    code === 22 ||
    code === 1014 ||
    /quota/i.test(error.message)
  );
};

/** UTF-16 bytes on disk, key and value both counted, largest first. */
export const measureLocalStorage = (
  storage: SyncStorageLike,
): { totalBytes: number; byKey: Array<[string, number]> } => {
  const byKey: Array<[string, number]> = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key === null) continue;
    byKey.push([key, (key.length + (storage.getItem(key)?.length ?? 0)) * 2]);
  }
  byKey.sort((a, b) => b[1] - a[1]);
  return { totalBytes: byKey.reduce((sum, [, bytes]) => sum + bytes, 0), byKey };
};

/** The origin's real budget and usage, when the browser will say. */
export const estimateDeviceStorage = async (): Promise<{ usage: number; quota: number } | null> => {
  try {
    const estimate = await globalThis.navigator?.storage?.estimate?.();
    if (!estimate || estimate.usage === undefined || estimate.quota === undefined) return null;
    return { usage: estimate.usage, quota: estimate.quota };
  } catch {
    return null;
  }
};

const removeLegacyKeys = (storage: SyncStorageLike): number => {
  let removed = 0;
  for (const key of LEGACY_LOCAL_STORAGE_KEYS) {
    if (storage.getItem(key) === null) continue;
    storage.removeItem(key);
    removed++;
  }
  return removed;
};

const describe = (error: unknown): string =>
  error instanceof Error ? `${error.name}: ${error.message}` : String(error);

export const createDeviceStorageBudget = (storage: SyncStorageLike, deps: DeviceStorageBudgetDeps) => {
  // How far down the ladder the current streak of failures has gone. Reset by
  // any write that fits, so the next streak starts at the cheap rung again.
  let rungsTaken = 0;
  let reportedExhausted = false;
  // Set while a shed is in progress: the rewrite it triggers must not start a
  // ladder of its own, only tell this one whether it fit.
  let shedding = false;
  let rewriteFit: boolean | null = null;
  // One ladder at a time. Writes that arrive meanwhile wait, latest per key.
  let laddering = false;
  const waiting = new Map<string, { value: string; settle: Array<{ resolve: () => void; reject: (e: unknown) => void }> }>();

  const report = (key: string, value: string, rung: BudgetRung, recovered: boolean, error: unknown) => {
    const { totalBytes, byKey } = measureLocalStorage(storage);
    deps.report({
      key,
      rung,
      recovered,
      writeBytes: (key.length + value.length) * 2,
      totalBytes,
      largestKeys: byKey.slice(0, REPORTED_KEYS).map(([name, bytes]) => [reportableKeyName(name), bytes]),
      error: describe(error),
    });
  };

  /** null when it fit; the quota error otherwise. Anything else propagates. */
  const attempt = (key: string, value: string): unknown => {
    try {
      storage.setItem(key, value);
      return null;
    } catch (error) {
      if (!isQuotaExceeded(error)) throw error;
      return error;
    }
  };

  const fitted = () => {
    rungsTaken = 0;
    reportedExhausted = false;
  };

  const ladder = async (key: string, value: string, error: unknown): Promise<void> => {
    laddering = true;
    try {
      // Off the failing write's call stack before anything may dispatch.
      await Promise.resolve();

      if (removeLegacyKeys(storage) > 0 && attempt(key, value) === null) {
        fitted();
        report(key, value, 'legacy-blob', true, error);
        return;
      }

      while (rungsTaken < SHED_TIERS.length) {
        const tier = SHED_TIERS[rungsTaken++];
        shedding = true;
        rewriteFit = null;
        try {
          deps.shed(tier);
        } catch (shedError) {
          shedding = false;
          report(key, value, tier, false, shedError);
          break;
        }
        shedding = false;
        report(key, value, tier, rewriteFit === true, error);
        if (rewriteFit === true) return;
      }

      if (!reportedExhausted) {
        reportedExhausted = true;
        report(key, value, 'exhausted', false, error);
      }
      throw error;
    } finally {
      laddering = false;
      drain();
    }
  };

  const drain = () => {
    const next = waiting.entries().next();
    if (next.done) return;
    const [key, { value, settle }] = next.value;
    waiting.delete(key);
    write(key, value).then(
      () => settle.forEach(s => s.resolve()),
      (e: unknown) => settle.forEach(s => s.reject(e)),
    );
  };

  const write = (key: string, value: string): Promise<void> => {
    if (shedding) {
      // The rewrite a shed triggered. Report the outcome to the ladder; never
      // start one of our own.
      let error: unknown;
      try {
        error = attempt(key, value);
      } catch (other) {
        rewriteFit = false;
        return Promise.reject(other);
      }
      rewriteFit = error === null;
      return error === null ? Promise.resolve() : Promise.reject(error);
    }

    if (laddering) {
      return new Promise<void>((resolve, reject) => {
        const entry = waiting.get(key);
        if (entry) {
          entry.value = value;
          entry.settle.push({ resolve, reject });
        } else {
          waiting.set(key, { value, settle: [{ resolve, reject }] });
        }
      });
    }

    let error: unknown;
    try {
      error = attempt(key, value);
    } catch (other) {
      return Promise.reject(other);
    }
    if (error === null) {
      fitted();
      return Promise.resolve();
    }
    return ladder(key, value, error);
  };

  return { write };
};

export type DeviceStorageBudget = ReturnType<typeof createDeviceStorageBudget>;

// ---------------------------------------------------------------------------
// The engine → store seam. Mirrors `registerPersistFlush` in the ratings
// outbox middleware: the store cannot be imported here (it imports this).

type LowStorageHandler = (tier: ShedTier) => void;
let lowStorageHandler: LowStorageHandler | null = null;

export const registerLowStorageHandler = (handler: LowStorageHandler | null): void => {
  lowStorageHandler = handler;
};

/** Throws rather than pretending: an unregistered handler is a wiring bug, not a shed. */
export const shedViaRegisteredHandler = (tier: ShedTier): void => {
  if (!lowStorageHandler) throw new Error('no low-storage handler registered');
  lowStorageHandler(tier);
};
